import React, { useEffect, useState } from 'react';
import './CookieConsent.css';

const STORAGE_KEY = 'sls_cookie_consent';

// EU/EEA + UK time zones. Used as a privacy-friendly, no-network heuristic to decide
// whether to SHOW the banner. The legal guarantee is enforced separately by Google
// Consent Mode's region defaults in index.html (which use Google's IP-based geo), so a
// missed/extra match here only affects whether the banner appears, never whether GA can
// set cookies for a protected visitor.
const EEA_UK_TIME_ZONES = new Set([
  // United Kingdom + Ireland
  'Europe/London', 'Europe/Dublin',
  // Western / Central Europe
  'Europe/Lisbon', 'Europe/Madrid', 'Europe/Paris', 'Europe/Brussels',
  'Europe/Amsterdam', 'Europe/Luxembourg', 'Europe/Berlin', 'Europe/Zurich',
  'Europe/Vienna', 'Europe/Rome', 'Europe/Malta', 'Europe/Copenhagen',
  'Europe/Oslo', 'Europe/Stockholm', 'Europe/Reykjavik', 'Europe/Vaduz',
  // Central / Eastern Europe
  'Europe/Prague', 'Europe/Bratislava', 'Europe/Budapest', 'Europe/Warsaw',
  'Europe/Ljubljana', 'Europe/Zagreb', 'Europe/Belgrade', 'Europe/Tallinn',
  'Europe/Riga', 'Europe/Vilnius', 'Europe/Helsinki', 'Europe/Athens',
  'Europe/Bucharest', 'Europe/Sofia',
  // Cyprus (Asia/* zones) + Atlantic islands belonging to EU members
  'Asia/Nicosia', 'Asia/Famagusta',
  'Atlantic/Canary', 'Atlantic/Madeira', 'Atlantic/Azores',
]);

function isLikelyEeaUkVisitor() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (EEA_UK_TIME_ZONES.has(tz)) return true;
    // Catch-all for any other Europe/* zone (Switzerland, Andorra, etc.) — erring toward
    // showing the banner in Europe is the safe, privacy-respecting default.
    return tz.startsWith('Europe/');
  } catch (e) {
    // If we can't determine the zone, default to showing the banner (safer).
    return true;
  }
}

function applyConsent(granted) {
  if (typeof window.gtag !== 'function') return;
  const value = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
}

function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let stored = null;
    try {
      stored = window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      stored = null;
    }

    // Returning visitor with a saved choice: re-apply it, never show the banner again.
    if (stored === 'granted' || stored === 'denied') {
      applyConsent(stored === 'granted');
      return;
    }

    // First visit with no choice: only EU/EEA/UK visitors see the banner.
    if (isLikelyEeaUkVisitor()) {
      setVisible(true);
    }
  }, []);

  const choose = (granted) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, granted ? 'granted' : 'denied');
    } catch (e) {
      /* ignore storage errors (private mode, etc.) */
    }
    applyConsent(granted);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="cookie-consent" role="dialog" aria-live="polite" aria-label="Cookie consent">
      <div className="cookie-consent__inner">
        <p className="cookie-consent__text">
          We use cookies to measure site traffic with Google Analytics. We only set these
          cookies if you accept. See how it helps us improve — your choice is remembered on
          this device.
        </p>
        <div className="cookie-consent__actions">
          <button
            type="button"
            className="cookie-consent__btn cookie-consent__btn--ghost"
            onClick={() => choose(false)}
          >
            Decline
          </button>
          <button
            type="button"
            className="cookie-consent__btn cookie-consent__btn--accept"
            onClick={() => choose(true)}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;
